import { SpeakerFaceCoordinatesResponse } from '@goldcast/api/content';
import { v4 as uuidv4 } from 'uuid';
import { CompositeSegment } from '../../../types/Segment/CompositeSegment';
import { SegmentType } from '../../../types/Segment/Type';
import { SpeakerLabel, VideoElement } from '../../../types/Element/VideoElement';
import { ElementType } from '../../../types/Element/Type';
import {
  getLayoutChangesAndPlayRanges,
  getLayoutChangesAndPlayRangesWithSplits
} from './getLayoutChangesAndPlayRanges';
import { getAlignedRecordings } from '../../../stores/alignedRecordings';
import { getSpeakerLabelConfig } from './getSpeakerInfo';
import { getVideoDetailsFromSpeaker } from './utils';
import { createScenesFromSceneOverrides } from './scenes';
import { UPLOAD_SCREENSHARE_ID, ORIGINAL_VIDEO_ID } from './constants';
import { doesRecordingHaveSpeaker, isRecordingScreenshare } from '../../../stores/videoSpeakerDetailsStore';
import { getSpeakerDetailsForBroadcast } from '../../../stores/speakerDetailsStore';
import { Clip } from '@/domains/asset';
import { DEFAULT_COLOR } from '@/libs/core';
import { SpeakerWithDetails } from '@/context/TranscriptContext/TranscriptContextTypes';
import {
  doesClipHaveScenes,
  getClipBroadcastId,
  isClipAudiogram,
  isClipFromRecordingStudio,
  isClipLayoutDefault
} from '@/libs/clipUtils';
import {
  isLiveEventRemotionWithPreviewAndAlignedStream,
  isSplitsInTimelineEnabled,
  isScreenShareEnabledForCustomUploadClips
} from '@/stores/clip';
import { SceneChanges } from '@/domains/event';
import { speakersAnalysisStore } from '@/stores/speakersAnalysis/speakersAnalysis';

const shouldShowSpeakerLabels = ({ recordingId, clip }: { recordingId: string; clip: Clip }) => {
  if (isClipLayoutDefault(clip) && !doesClipHaveScenes(clip)) {
    /** ignore this check if scene exists as scenes open with new timeline which uses original video which handles this*/
    return false;
  }
  if (!doesRecordingHaveSpeaker({ broadcastId: getClipBroadcastId(clip)!, recordingId })) {
    // speaker details not found for recording
    return false;
  }
  // speaker details found for recording
  if (isClipAudiogram(clip)) {
    // audiogram clips always show speaker labels
    return true;
  }

  const isScreenshare = isRecordingScreenshare({ broadcastId: getClipBroadcastId(clip)!, recordingId });
  const isSharedVideo = isRecordingScreenshare({ broadcastId: getClipBroadcastId(clip)!, recordingId });

  if (isScreenshare || isSharedVideo) {
    return false;
  }

  return !!clip.asset_metadata.magicLayout?.showSpeakerLabels;
};

const audiogramContainerStyle = { visibility: 'hidden', width: '1px', height: '1px' };

const getOriginalVideoUri = (clip: Clip) => {
  const { preview, source } = clip.content;
  return { preview, source };
};

const getOriginalVideo = ({
  clip,
  fromFrame,
  toFrame
}: {
  clip: Clip;
  fromFrame: number;
  toFrame: number;
}): VideoElement => {
  const { preview, source } = getOriginalVideoUri(clip);
  const name = 'Original Video';
  return {
    id: ORIGINAL_VIDEO_ID,
    name,
    type: ElementType.VIDEO,
    fromFrame,
    toFrame,
    details: {
      src: source,
      preview,
      altText: name,
      volume: [
        {
          value: 100,
          fromFrame,
          toFrame
        }
      ]
    },
    trim: [],
    style: {},
    containerStyle: {},
    speakerLabel: undefined,
    speakerDetails: undefined,
    isOriginalVideo: true
  };
};

const getAlignedStreamVideos = ({
  clip,
  fromFrame,
  toFrame
}: {
  clip: Clip;
  fromFrame: number;
  toFrame: number;
}): VideoElement[] => {
  const videos: VideoElement[] = [];
  getAlignedRecordings(getClipBroadcastId(clip)).forEach((recording, index) => {
    let speakerLabel: SpeakerLabel | null = null;
    let speakerDetails: SpeakerWithDetails | null = null;

    const shouldGetSpeakerInfo = shouldShowSpeakerLabels({ recordingId: recording.id, clip });

    if (shouldGetSpeakerInfo) {
      const speakerInfo = getSpeakerLabelConfig(recording.id, clip);

      speakerLabel = speakerInfo.speakerLabel;
      speakerDetails = speakerInfo.speakerDetails;
    }

    const containerStyle = isClipAudiogram(clip) ? audiogramContainerStyle : {};

    videos.push({
      id: recording.id,
      name: 'Video ' + recording.entity + index,
      type: ElementType.VIDEO,
      fromFrame,
      toFrame,
      details: {
        src: recording.mp4_url.replace(/([^:])\/\//g, '$1/'),
        preview: recording.preview_url,
        altText: 'Video ' + recording.entity + index,
        volume: [
          {
            value: 100,
            fromFrame,
            toFrame
          }
        ]
      },
      trim: [],
      style: {},
      containerStyle,
      speakerLabel: shouldGetSpeakerInfo && speakerLabel ? speakerLabel : undefined,
      speakerDetails: shouldGetSpeakerInfo && speakerDetails ? speakerDetails : undefined,
      isOriginalVideo: false,
      aspectRatio: recording.video_metadata
        ? // TODO: @@HiteshChoudhary0098 this needs to be revisited. What backend stopped sending types for transcript mapping.
          // @ts-ignore
          recording.video_metadata.width / recording.video_metadata.height
        : undefined
    });
  });
  return videos;
};

const getScreenshareVideo = ({
  clip,
  fromFrame,
  toFrame
}: {
  clip: Clip;
  fromFrame: number;
  toFrame: number;
}): VideoElement | null => {
  const speakersAnalysis = speakersAnalysisStore.getSnapshot()[clip.id];

  if (speakersAnalysis?.screenshare_bounds && speakersAnalysis.screenshare_bounds.length > 0) {
    const screenshareVideoId = UPLOAD_SCREENSHARE_ID;
    const { preview: screensharePreview, source: screenshareSource } = getOriginalVideoUri(clip);

    return {
      id: screenshareVideoId,
      name: 'upload_screenshare',
      type: ElementType.VIDEO,
      fromFrame,
      toFrame,
      details: {
        src: screenshareSource,
        preview: screensharePreview,
        altText: 'Screenshare',
        volume: [
          {
            value: 100,
            fromFrame,
            toFrame
          }
        ]
      },
      trim: [],
      style: {},
      containerStyle: {},
      speakerLabel: undefined,
      speakerDetails: undefined,
      isOriginalVideo: false
    };
  }

  return null;
};

/**
 * Creates video elements for both event recordings and uploaded videos.
 *
 * @description
 * Currently handles both event recordings and uploaded videos in a single function.
 * This shared implementation may be split into separate functions if future requirements
 * cause these video types to evolve independently.
 *
 * @param {Object} params - Configuration parameters for video generation
 * @param {Clip} params.clip - The clip containing asset metadata and content information
 * @param {number} params.fromFrame - The starting frame number for the video
 * @param {number} params.toFrame - The ending frame number for the video
 * @returns {VideoElement[]} Array of video elements with speaker information and styling
 */
const getVideosForEventAndUploads = ({
  clip,
  fromFrame,
  toFrame
}: {
  clip: Clip;
  fromFrame: number;
  toFrame: number;
}): VideoElement[] => {
  const videos: VideoElement[] = [];

  const speakersWithDetails = getSpeakerDetailsForBroadcast(getClipBroadcastId(clip)!);

  const { preview, source } = getOriginalVideoUri(clip);

  Object.entries(speakersWithDetails).forEach(([_, speaker]) => {
    const { videoId, name } = getVideoDetailsFromSpeaker(speaker);
    let speakerLabel: SpeakerLabel | undefined = undefined;
    const showSpeakerLabels = shouldShowSpeakerLabels({ recordingId: videoId, clip });

    if (showSpeakerLabels) {
      speakerLabel = getSpeakerLabelConfig(videoId, clip).speakerLabel;
    }

    const containerStyle = isClipAudiogram(clip) ? audiogramContainerStyle : {};
    videos.push({
      id: videoId,
      name,
      type: ElementType.VIDEO,
      fromFrame,
      toFrame,
      details: {
        src: source,
        preview,
        altText: name,
        volume: [
          {
            value: 100,
            fromFrame,
            toFrame
          }
        ]
      },
      trim: [],
      style: {},
      containerStyle,
      speakerLabel,
      speakerDetails: showSpeakerLabels ? speaker : undefined,
      isOriginalVideo: false
    });
  });

  return videos;
};

const getAlignedStreamAndOriginalVideos = ({
  clip,
  fromFrame,
  toFrame
}: {
  clip: Clip;
  fromFrame: number;
  toFrame: number;
}) => {
  const videos = getAlignedStreamVideos({ clip, fromFrame, toFrame });
  videos.push(getOriginalVideo({ clip, fromFrame, toFrame }));
  return videos;
};

const getVideos = ({ clip, fromFrame, toFrame }: { clip: Clip; fromFrame: number; toFrame: number }) => {
  let videos: VideoElement[] = [];

  if (isLiveEventRemotionWithPreviewAndAlignedStream(clip)) {
    videos = getAlignedStreamAndOriginalVideos({ clip, fromFrame, toFrame });
  } else if (isClipFromRecordingStudio(clip)) {
    videos = getAlignedStreamVideos({ clip, fromFrame, toFrame });
  } else {
    videos = [
      ...getVideosForEventAndUploads({ clip, fromFrame, toFrame }),
      getOriginalVideo({ clip, fromFrame, toFrame })
    ];
    if (isScreenShareEnabledForCustomUploadClips(clip)) {
      const screenshareVideo = getScreenshareVideo({ clip, fromFrame, toFrame });
      if (screenshareVideo) {
        videos.push(screenshareVideo);
      }
    }
  }

  return videos;
};

export function createMainCompositeSegment({
  clip,
  fps,
  duration,
  offset,
  sceneOverrides,
  facialRecognitionToDefaultPosition
}: {
  clip: Clip;
  fps: number;
  duration: number;
  offset: number;
  sceneOverrides?: SceneChanges;
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
}): CompositeSegment {
  const fromFrame = clip.asset_metadata.start * fps;
  const toFrame = clip.asset_metadata.end * fps;
  const videos: VideoElement[] = getVideos({ clip, fromFrame, toFrame });

  const asset_metadata = clip.asset_metadata;
  const layout = asset_metadata.layout;
  const size = asset_metadata.size;
  const fitMode = asset_metadata.fit;
  const hideBorders = !!asset_metadata.hide_borders;

  let scenes = asset_metadata.scenes || [];
  if (sceneOverrides) {
    scenes = createScenesFromSceneOverrides({
      sceneOverrides,
      facialRecognitionToDefaultPosition,
      fps,
      clipStartFrame: fromFrame,
      clipEndFrame: toFrame,
      videos,
      recordings: getAlignedRecordings(getClipBroadcastId(clip))
    });
  }

  const { layoutChanges, playRanges } = getLayoutChangesAndPlayRanges({
    clip,
    fps,
    layout,
    size,
    fitMode,
    hideBorders,
    scenes,
    videos
  });

  return {
    id: 'segment',
    name: 'Composite',
    type: SegmentType.COMPOSITE,
    scenes,
    videos,
    layoutChanges,
    playRanges,
    fromFrame: offset,
    toFrame: offset + duration,
    details: {
      background: {
        type: asset_metadata.magicLayout?.backgroundImage ? 'IMAGE_URL' : 'COLOR',
        value:
          asset_metadata.magicLayout?.backgroundImage || asset_metadata.magicLayout?.backgroundColor || DEFAULT_COLOR
      }
    },
    projectId: clip.content.project.id,
    contentId: clip.content.id,
    maxBounds: {
      fromFrame,
      toFrame
    },
    currentBounds: {
      fromFrame,
      toFrame
    }
  };
}

// This should only be used with splits
export function createCompositeFromOldComposite({
  clip,
  fps,
  duration,
  offset,
  sceneOverrides,
  oldCompositeSegment,
  facialRecognitionToDefaultPosition
}: {
  clip: Clip;
  fps: number;
  duration: number;
  offset: number;
  sceneOverrides?: SceneChanges;
  oldCompositeSegment?: CompositeSegment;
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
}): CompositeSegment {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  if (!splitsEnabled) {
    throw new Error('Splits are not enabled for this clip');
  }

  if (!oldCompositeSegment || !oldCompositeSegment.segment_metadata) {
    throw new Error('Old composite segment not found or no metadata found');
  }

  const fromFrame = oldCompositeSegment.segment_metadata.start * fps;
  const toFrame = oldCompositeSegment.segment_metadata.end * fps;
  const videos: VideoElement[] = getVideos({ clip, fromFrame, toFrame });

  const asset_metadata = clip.asset_metadata;
  const layout = asset_metadata.layout;
  const size = asset_metadata.size;
  const fitMode = asset_metadata.fit;
  const hideBorders = !!asset_metadata.hide_borders;

  let scenes = oldCompositeSegment.segment_metadata?.scenes || [];
  if (sceneOverrides) {
    scenes = createScenesFromSceneOverrides({
      sceneOverrides,
      facialRecognitionToDefaultPosition,
      fps,
      clipStartFrame: fromFrame,
      clipEndFrame: toFrame,
      videos,
      recordings: getAlignedRecordings(getClipBroadcastId(clip))
    });
  }

  const { layoutChanges, playRanges } = getLayoutChangesAndPlayRangesWithSplits({
    clip,
    fps,
    layout,
    size,
    fitMode,
    hideBorders,
    scenes,
    videos,
    oldSegment: oldCompositeSegment
  });

  return {
    id: oldCompositeSegment.id,
    name: oldCompositeSegment.name,
    type: SegmentType.COMPOSITE,
    scenes,
    videos,
    layoutChanges,
    playRanges,
    fromFrame: offset,
    toFrame: offset + duration,
    details: {
      background: {
        type: asset_metadata.magicLayout?.backgroundImage ? 'IMAGE_URL' : 'COLOR',
        value:
          asset_metadata.magicLayout?.backgroundImage || asset_metadata.magicLayout?.backgroundColor || DEFAULT_COLOR
      }
    },
    segment_metadata: oldCompositeSegment?.segment_metadata || {
      start: clip.asset_metadata.start,
      end: clip.asset_metadata.end,
      deletes: clip.asset_metadata.deletes || {},
      edits: clip.asset_metadata.edits || {},
      merges: clip.asset_metadata.merges || [],
      excludes: clip.asset_metadata.excludes || [],
      maxVideoDuration: clip.asset_metadata.maxVideoDuration || 0,
      captions: clip.asset_metadata.captions || [],
      fitMode: clip.asset_metadata.fit,
      scenes: clip.asset_metadata.scenes || [],
      duration: clip.asset_metadata.duration || 0
    },
    projectId: clip.content.project.id,
    contentId: clip.content.id,
    maxBounds: {
      fromFrame,
      toFrame
    },
    currentBounds: {
      fromFrame,
      toFrame
    }
  };
}

export function createMainCompositeSegmentWithMetaData({
  clip,
  fps,
  duration,
  offset,
  sceneOverrides,
  facialRecognitionToDefaultPosition
}: {
  clip: Clip;
  fps: number;
  duration: number;
  offset: number;
  sceneOverrides?: SceneChanges;
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
}): CompositeSegment {
  const fromFrame = clip.asset_metadata.start * fps;
  const toFrame = clip.asset_metadata.end * fps;
  const videos: VideoElement[] = getVideos({ clip, fromFrame, toFrame });

  const asset_metadata = clip.asset_metadata;
  const layout = asset_metadata.layout;
  const size = asset_metadata.size;
  const fitMode = asset_metadata.fit;
  const hideBorders = !!asset_metadata.hide_borders;

  let scenes = asset_metadata.scenes || [];
  if (sceneOverrides) {
    scenes = createScenesFromSceneOverrides({
      sceneOverrides,
      facialRecognitionToDefaultPosition,
      fps,
      clipStartFrame: fromFrame,
      clipEndFrame: toFrame,
      videos,
      recordings: getAlignedRecordings(getClipBroadcastId(clip))
    });
  }

  const { layoutChanges, playRanges } = getLayoutChangesAndPlayRanges({
    clip,
    fps,
    layout,
    size,
    fitMode,
    hideBorders,
    scenes,
    videos
  });

  return {
    id: uuidv4(),
    name: 'Composite',
    type: SegmentType.COMPOSITE,
    scenes,
    videos,
    layoutChanges,
    playRanges,
    fromFrame: offset,
    toFrame: offset + duration,
    details: {
      background: {
        type: asset_metadata.magicLayout?.backgroundImage ? 'IMAGE_URL' : 'COLOR',
        value:
          asset_metadata.magicLayout?.backgroundImage || asset_metadata.magicLayout?.backgroundColor || DEFAULT_COLOR
      }
    },
    projectId: clip.content.project.id,
    contentId: clip.content.id,
    maxBounds: {
      fromFrame,
      toFrame
    },
    currentBounds: {
      fromFrame,
      toFrame
    },
    segment_metadata: {
      start: clip.asset_metadata.start,
      end: clip.asset_metadata.end,
      deletes: clip.asset_metadata.deletes || {},
      edits: clip.asset_metadata.edits || {},
      merges: clip.asset_metadata.merges || [],
      excludes: clip.asset_metadata.excludes || [],
      maxVideoDuration: clip.asset_metadata.maxVideoDuration || 0,
      captions: clip.asset_metadata.captions || [],
      fitMode: clip.asset_metadata.fit,
      scenes: clip.asset_metadata.scenes || [],
      duration: clip.asset_metadata.duration || 0
    }
  };
}
